import React from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Package, CheckCircle2, Loader2, LogOut, Phone, History,
  BarChart3, FileDown, ClipboardList, Wallet, Inbox, Printer,
  RotateCcw, ScanLine, AlertTriangle, Bluetooth, Camera, Zap, ZapOff,
  Cpu, FileText
} from "lucide-react";
import useBarcodeScanner from "../hooks/useBarcodeScanner";
import CameraQRScanner from "../components/CameraQRScanner";
import EquipmentTab from "../components/EquipmentTab";
import StatementsTab from "../components/StatementsTab";
import { connectPrinter, printLabel, disconnectPrinter, isPrinterSupported } from "../lib/thermal-printer";
import { isNative, initShopNativePush, flushPendingShopToken } from "../lib/shop-native";
import useShopRealtime from "../hooks/useShopRealtime";
import RealtimeBanner from "../components/RealtimeBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ema_pakkeshop_token";
const SHOP_KEY = "ema_pakkeshop_shop";

const fmtDkk = (n) => `${Number(n || 0).toLocaleString("da-DK", { maximumFractionDigits: 2 })} kr`;
const fmtTs = (s) => (s || "").slice(0, 16).replace("T", " kl. ");

function LoginCard({ onLogin }) {
  const [email, setEmail] = React.useState("");
  const [pin, setPin] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pakkeshop/login`, {
        email: email.trim().toLowerCase(),
        pin: pin.trim(),
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(SHOP_KEY, JSON.stringify(data.shop));
      onLogin(data.token, data.shop);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login fejlede");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1128] flex items-center justify-center px-5 py-12">
      <div className="max-w-md w-full bg-white rounded-xl p-8 border border-[#E2E8F0] shadow-xl">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">EMA Pickup Point Portal</div>
        <h1 className="font-display font-black text-3xl text-[#0A1128] mt-2 tracking-tight">Log ind</h1>
        <p className="text-[#475569] mt-2 text-sm">
          Brug din ejer-email og den 4-cifrede PIN-kode du modtog i velkomst-emailen.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="shop-login-form">
          <div>
            <label className="label-flat">Ejer-email</label>
            <input
              className="input-flat"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="din@email.dk"
              required
              data-testid="shop-email"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="label-flat">PIN-kode (4 cifre)</label>
            <input
              className="input-flat font-mono text-2xl text-center tracking-[8px]"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              maxLength={4}
              required
              data-testid="shop-pin"
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </div>
          <button
            type="submit"
            disabled={busy || pin.length !== 4 || !email}
            className="btn-primary w-full justify-center"
            data-testid="shop-login-submit"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : "Log ind"}
          </button>
          <p className="text-xs text-brand-text2 text-center">
            Glemt PIN? Skriv til <a href="mailto:partner@emakurer.dk" className="text-brand-red">partner@emakurer.dk</a>
          </p>
        </form>
      </div>
    </div>
  );
}

function ParcelsTab({ token, parcels, loading, onReload, printer, onBluetoothPrint }) {
  const [handoverCode, setHandoverCode] = React.useState({});
  const [busy, setBusy] = React.useState({});

  const scanIn = async (booking_id) => {
    setBusy((s) => ({ ...s, [booking_id]: true }));
    try {
      await axios.post(`${API}/pakkeshop/parcels/${booking_id}/scan-in`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Scannet ind — kunden er nu underrettet");
      await onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke scanne ind");
    } finally {
      setBusy((s) => ({ ...s, [booking_id]: false }));
    }
  };

  const printLabelPdf = async (booking_id, booking_number) => {
    try {
      const res = await axios.get(`${API}/pakkeshop/parcels/${booking_id}/label.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `label-${booking_number}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("Kunne ikke hente label");
    }
  };

  const handover = async (booking_id, booking_number) => {
    const code = (handoverCode[booking_id] || "").trim();
    if (!code || code.length !== 6) {
      toast.error("Pickup-koden er 6 tegn");
      return;
    }
    setBusy((s) => ({ ...s, [booking_id]: true }));
    try {
      await axios.post(
        `${API}/pakkeshop/parcels/${booking_id}/handover`,
        { pickup_code: code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Udleveret. Kommission registreret.");
      setHandoverCode((s) => ({ ...s, [booking_id]: "" }));
      await onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Forkert kode");
    } finally {
      setBusy((s) => ({ ...s, [booking_id]: false }));
    }
  };

  if (loading) {
    return <div className="text-[#475569] text-center py-10" data-testid="parcels-loading">Indlæser pakker...</div>;
  }
  if (parcels.length === 0) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-10 text-center text-[#475569]" data-testid="parcels-empty">
        <Inbox size={32} className="mx-auto opacity-50 mb-3" />
        Ingen pakker venter på udlevering lige nu.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="parcels-list">
      {parcels.map((p) => {
        const scannedIn = !!p.shop_scanned_in_at;
        return (
          <div key={p.id} className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={`parcel-${p.booking_number}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-mono font-bold text-[#0A1128]">#{p.booking_number}</div>
                  {scannedIn ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider bg-amber-100 text-amber-800 border-amber-200">
                      Klar til udlevering
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider bg-blue-100 text-blue-800 border-blue-200">
                      Skal scannes ind
                    </span>
                  )}
                </div>
                <div className="font-display font-bold text-base text-[#0A1128] mt-1">{p.customer_name}</div>
                {p.customer_phone && (
                  <a href={`tel:${p.customer_phone}`} className="text-sm text-[#475569] flex items-center gap-1 mt-1 hover:text-brand-red">
                    <Phone size={11} /> {p.customer_phone}
                  </a>
                )}
                <div className="text-xs text-[#475569] mt-1">Antal: {p.package_count} pakke(r)</div>
                <div className="text-xs text-[#475569] mt-1">Modtaget: {fmtTs(p.shop_delivered_at)}</div>
              </div>
              <div className="w-full sm:w-auto flex flex-col gap-2">
                {!scannedIn ? (
                  <button
                    onClick={() => scanIn(p.id)}
                    disabled={busy[p.id]}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg px-4 py-2.5 text-sm flex items-center justify-center gap-2"
                    data-testid={`scan-in-${p.booking_number}`}
                  >
                    {busy[p.id] ? <Loader2 className="animate-spin" size={14} /> : <><ScanLine size={14} /> Scan ind</>}
                  </button>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <input
                        value={handoverCode[p.id] || ""}
                        onChange={(e) => setHandoverCode((s) => ({ ...s, [p.id]: e.target.value.toUpperCase().slice(0, 6) }))}
                        placeholder="KODE"
                        className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2 font-mono text-lg uppercase tracking-widest text-center w-32 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red"
                        maxLength={6}
                        data-testid={`code-${p.booking_number}`}
                      />
                      <button
                        onClick={() => handover(p.id, p.booking_number)}
                        disabled={busy[p.id]}
                        className="btn-primary text-sm"
                        data-testid={`handover-${p.booking_number}`}
                      >
                        {busy[p.id] ? <Loader2 className="animate-spin" size={14} /> : <><CheckCircle2 size={14} /> Udlever</>}
                      </button>
                    </div>
                    <button
                      onClick={() => printLabelPdf(p.id, p.booking_number)}
                      className="text-xs text-[#475569] hover:text-[#0A1128] flex items-center justify-center gap-1.5 mt-1"
                      data-testid={`print-label-${p.booking_number}`}
                    >
                      <Printer size={12} /> Print PDF-label
                    </button>
                    {printer && (
                      <button
                        onClick={() => onBluetoothPrint(p, false)}
                        className="text-xs text-emerald-700 hover:text-emerald-900 flex items-center justify-center gap-1.5"
                        data-testid={`bt-print-${p.booking_number}`}
                      >
                        <Bluetooth size={12} /> Print via Bluetooth
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HistoryTab({ token }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/pakkeshop/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (alive) setItems(data.parcels || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  if (loading) return <div className="text-brand-text2 text-center py-10" data-testid="history-loading">Indlæser historik...</div>;
  if (items.length === 0) {
    return (
      <div className="bg-white border border-brand p-10 text-center text-brand-text2" data-testid="history-empty">
        <History size={32} className="mx-auto opacity-50 mb-3" />
        Ingen udleverede pakker endnu.
      </div>
    );
  }
  return (
    <div className="bg-white border border-brand overflow-hidden" data-testid="history-list">
      <table className="w-full text-sm">
        <thead className="bg-brand-bg text-brand-text2 text-xs uppercase tracking-widest">
          <tr>
            <th className="text-left p-3">Booking</th>
            <th className="text-left p-3">Kunde</th>
            <th className="text-left p-3">Modtaget</th>
            <th className="text-left p-3">Udleveret</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} className="border-t border-brand" data-testid={`history-${p.booking_number}`}>
              <td className="p-3 font-mono font-bold text-brand-dark">#{p.booking_number}</td>
              <td className="p-3">{p.customer_name || "—"}</td>
              <td className="p-3 text-brand-text2">{fmtTs(p.shop_delivered_at)}</td>
              <td className="p-3 text-brand-text2">{fmtTs(p.shop_handed_over_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatsTab({ token }) {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/pakkeshop/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (alive) setStats(data); })
      .catch(() => { if (alive) setStats(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  if (loading) return <div className="text-[#475569] text-center py-10" data-testid="stats-loading">Indlæser...</div>;
  if (!stats) return <div className="text-[#475569] text-center py-10">Kunne ikke hente statistik.</div>;

  return (
    <div className="space-y-5" data-testid="stats-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">Modtaget</div>
          <div className="font-display font-black text-3xl text-[#0A1128] mt-2 tracking-tight" data-testid="stats-received">{stats.received_this_month}</div>
          <div className="text-xs text-[#475569] mt-1">denne måned</div>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">Udleveret</div>
          <div className="font-display font-black text-3xl text-[#0A1128] mt-2 tracking-tight" data-testid="stats-handed-over">{stats.handed_over_this_month}</div>
          <div className="text-xs text-[#475569] mt-1">denne måned</div>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">Returneret</div>
          <div className="font-display font-black text-3xl text-[#0A1128] mt-2 tracking-tight" data-testid="stats-returned">{stats.returned_this_month || 0}</div>
          <div className="text-xs text-[#475569] mt-1">denne måned</div>
        </div>
        <div className="bg-[#0A1128] text-white border border-brand-red rounded-xl p-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Kommission</div>
          <div className="font-display font-black text-3xl text-white mt-2 tracking-tight" data-testid="stats-commission">{fmtDkk(stats.estimated_commission_dkk)}</div>
          <div className="text-xs text-white/60 mt-1">udbetales 1. i næste md</div>
        </div>
      </div>
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
        <div className="flex items-center gap-2 text-[#0A1128] font-bold mb-2">
          <Wallet size={16} /> Kommissionssatser
        </div>
        <div className="text-sm text-[#475569] leading-relaxed grid sm:grid-cols-3 gap-3 mt-3">
          <div><strong className="text-[#0A1128]">{fmtDkk(stats.rate_per_received_dkk)}</strong> per modtaget pakke</div>
          <div><strong className="text-[#0A1128]">{fmtDkk(stats.rate_per_handover_dkk)}</strong> per udleveret pakke</div>
          <div><strong className="text-[#0A1128]">{fmtDkk(stats.rate_per_return_dkk)}</strong> per retur-håndtering</div>
        </div>
        <div className="text-xs text-[#475569] mt-3">Måneden løber 1.–sidste dato. Opgørelsen sendes til din ejer-email.</div>
      </div>
    </div>
  );
}

function ReturnsTab({ token, onReload, printer, onBluetoothPrint }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(new Set());
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pakkeshop/returns`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data.parcels || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const printReturnLabel = async (booking_id, booking_number) => {
    try {
      const res = await axios.get(`${API}/pakkeshop/parcels/${booking_id}/return-label.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `retur-${booking_number}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("Kunne ikke hente retur-label");
    }
  };

  const handoverReturns = async () => {
    if (selected.size === 0) {
      toast.error("Vælg mindst én pakke");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/pakkeshop/returns/handover-to-courier`,
        { booking_ids: Array.from(selected) },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${selected.size} pakke(r) klargjort til retur — kurer henter snart`);
      setSelected(new Set());
      await load();
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke afslutte");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-[#475569] text-center py-10" data-testid="returns-loading">Indlæser returkø...</div>;
  if (items.length === 0) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-10 text-center text-[#475569]" data-testid="returns-empty">
        <RotateCcw size={32} className="mx-auto opacity-50 mb-3" />
        Ingen pakker i returkø. Pakker markeres til retur efter 7 dage uden afhentning.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="returns-panel">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900 leading-relaxed">
          <strong>Returkø:</strong> Pakker der ikke er afhentet inden for 7 dage. Print retur-label, klargør dem til afhentning, og marker som "klar til kurer" når EMA-chauffør kommer.
        </div>
      </div>
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={`return-${p.booking_number}`}>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selected.has(p.id)}
                onChange={() => toggle(p.id)}
                className="mt-1.5"
                data-testid={`return-check-${p.booking_number}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-mono font-bold text-[#0A1128]">#{p.booking_number}</div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider bg-amber-100 text-amber-800 border-amber-200">Retur</span>
                </div>
                <div className="font-display font-bold text-base text-[#0A1128] mt-1">{p.customer_name}</div>
                {p.customer_phone && <div className="text-sm text-[#475569] mt-1 flex items-center gap-1"><Phone size={11} />{p.customer_phone}</div>}
                <div className="text-xs text-[#475569] mt-1">Lå i butik fra: {fmtTs(p.shop_delivered_at)}</div>
                <div className="text-xs text-[#475569]">Markeret retur: {fmtTs(p.return_marked_at)}</div>
              </div>
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={() => printReturnLabel(p.id, p.booking_number)}
                  className="text-xs text-[#475569] hover:text-[#0A1128] flex items-center gap-1.5 px-3 py-2"
                  data-testid={`return-label-${p.booking_number}`}
                >
                  <Printer size={12} /> Print PDF-label
                </button>
                {printer && (
                  <button
                    onClick={() => onBluetoothPrint(p, true)}
                    className="text-xs text-emerald-700 hover:text-emerald-900 flex items-center gap-1.5 px-3 py-1"
                    data-testid={`bt-return-${p.booking_number}`}
                  >
                    <Bluetooth size={12} /> Via Bluetooth
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={handoverReturns}
        disabled={busy || selected.size === 0}
        className="btn-primary w-full justify-center"
        data-testid="returns-handover-courier"
      >
        {busy ? <Loader2 className="animate-spin" size={14} /> : <><CheckCircle2 size={14} /> Marker {selected.size} pakke(r) som klar til kurer</>}
      </button>
    </div>
  );
}

function FlyerTab({ token, shop }) {
  const [busy, setBusy] = React.useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/pakkeshop/flyer.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EMA-Pakkeshop-${(shop?.shop_name || "flyer").replace(/[^a-z0-9]+/gi, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Flyer downloadet — print den i A4");
    } catch {
      toast.error("Kunne ikke hente flyer");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-brand p-7" data-testid="flyer-panel">
      <div className="flex items-start gap-4">
        <div className="bg-brand-red text-white p-3"><ClipboardList size={22} /></div>
        <div className="flex-1">
          <div className="font-display font-bold text-2xl text-brand-dark">Din butiks-flyer</div>
          <p className="text-brand-text2 mt-2 text-sm leading-relaxed">
            Print denne plakat og hæng den synligt i butikken. Den indeholder din butiks navn, adresse, åbningstider
            og en personlig QR-kode der sender kunder direkte til emakurer.dk med <strong>{shop?.shop_name}</strong> som
            valgt leveringsadresse. Jo flere kunder der vælger din butik, jo mere kommission tjener du.
          </p>
          <ul className="text-sm text-brand-text2 mt-4 space-y-1 list-disc list-inside">
            <li>Format: A4, klar til print</li>
            <li>QR-kode personliggjort til din butik</li>
            <li>Genereres on-demand med aktuelle data</li>
          </ul>
          <button onClick={download} disabled={busy} className="btn-primary mt-5" data-testid="flyer-download">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <><FileDown size={14} /> Download flyer (PDF)</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScannerPrinterBar({ token, printer, onConnectPrinter, onDisconnectPrinter, connectingPrinter, onCameraOpen, onScan }) {
  const [autoScan, setAutoScan] = React.useState(true);
  useBarcodeScanner((code) => {
    if (autoScan) onScan(code);
  }, { enabled: autoScan, minLength: 4 });

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="scanner-printer-bar">
      <button
        onClick={() => setAutoScan((s) => !s)}
        className={`flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg border transition-colors ${
          autoScan ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-50 border-slate-300 text-slate-500"
        }`}
        title={autoScan ? "Klar til scanner — tryk for at slå fra" : "Scanner deaktiveret"}
        data-testid="scanner-toggle"
      >
        {autoScan ? <Zap size={14} /> : <ZapOff size={14} />}
        {autoScan ? "Scanner ON" : "Scanner OFF"}
      </button>
      <button
        onClick={onCameraOpen}
        className="flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors"
        data-testid="camera-scan-btn"
      >
        <Camera size={14} /> Scan med kamera
      </button>
      <div className="flex-1" />
      {isPrinterSupported() && (
        printer ? (
          <button
            onClick={onDisconnectPrinter}
            className="flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg bg-emerald-50 border border-emerald-300 text-emerald-800 hover:bg-emerald-100 transition-colors"
            title={`Forbundet: ${printer.name}`}
            data-testid="printer-disconnect"
          >
            <Bluetooth size={14} /> Printer forbundet
          </button>
        ) : (
          <button
            onClick={onConnectPrinter}
            disabled={connectingPrinter}
            className="flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors"
            data-testid="printer-connect"
          >
            {connectingPrinter ? <Loader2 className="animate-spin" size={14} /> : <Bluetooth size={14} />}
            Tilslut thermal-printer
          </button>
        )
      )}
    </div>
  );
}

function ShopDashboard({ token, shop, onLogout }) {
  const [parcels, setParcels] = React.useState([]);
  const [returnCount, setReturnCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const t = sp.get("tab");
      if (t && ["parcels", "returns", "history", "stats", "statements", "equipment", "flyer"].includes(t)) return t;
    } catch { /* ignore */ }
    return "parcels";
  });
  const [printer, setPrinter] = React.useState(null);
  const [connectingPrinter, setConnectingPrinter] = React.useState(false);
  const [cameraOpen, setCameraOpen] = React.useState(false);
  // Pending pickup-code prompt — set when a scan returns awaiting_pickup_code
  const [codePromptBooking, setCodePromptBooking] = React.useState(null);
  const [promptCode, setPromptCode] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [parcelsRes, statsRes] = await Promise.all([
        axios.get(`${API}/pakkeshop/parcels`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pakkeshop/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setParcels(parcelsRes.data.parcels || []);
      setReturnCount(statsRes.data.return_queue || 0);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  React.useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, [load]);

  // Realtime SSE banner — instant feedback når en pakke ankommer eller en retur opstår
  const [liveEvent, setLiveEvent] = React.useState(null);
  const handleLiveEvent = React.useCallback((ev) => {
    setLiveEvent(ev);
    // Refresh parcels/returns in background
    load();
    // Sonner toast for users who already closed the banner
    if (ev?.event === "parcel_arrived") toast.success(ev.title || "Ny pakke ankommet");
    else if (ev?.event === "return_added") toast.warning(ev.title || "Ny retur");
  }, [load]);
  useShopRealtime(token, handleLiveEvent);

  const handleScan = React.useCallback(async (code) => {
    if (!code || code.length < 4) return;
    try {
      const { data } = await axios.post(`${API}/pakkeshop/scan`, { code }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.action === "scanned_in") {
        toast.success(`✅ Scannet ind: #${data.booking_number} (${data.customer_name})`);
        await load();
      } else if (data.action === "awaiting_pickup_code") {
        // Open prompt to enter pickup code
        setCodePromptBooking(data);
        setPromptCode("");
        setTab("parcels");
      } else if (data.action === "handover_completed") {
        toast.success(`✅ Udleveret: #${data.booking_number} — kommission ${data.commission_dkk} kr`);
        await load();
      } else if (data.action === "return_pending") {
        toast.warning(`📦 ${data.message}: #${data.booking_number}`);
        setTab("returns");
      } else if (data.action === "dropoff_completed") {
        toast.success(`✅ Drop-off modtaget: ${data.parcel_id || data.message}`);
        await load();
      } else if (data.action === "already_completed") {
        toast.info(`${data.message}: #${data.booking_number}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ukendt kode");
    }
  }, [token, load]);

  const handleConnectPrinter = async () => {
    setConnectingPrinter(true);
    try {
      const p = await connectPrinter();
      setPrinter(p);
      toast.success(`Printer forbundet: ${p.name}`);
    } catch (e) {
      toast.error(e?.message || "Kunne ikke forbinde printer");
    } finally {
      setConnectingPrinter(false);
    }
  };
  const handleDisconnectPrinter = async () => {
    await disconnectPrinter(printer);
    setPrinter(null);
    toast.info("Printer afbrudt");
  };

  const handleBluetoothPrint = async (booking, isReturn = false) => {
    if (!printer) {
      toast.error("Forbind først en thermal-printer via Bluetooth-knappen øverst");
      return;
    }
    try {
      await printLabel(printer, {
        booking_number: (booking.booking_number || "") + (isReturn ? "-R" : ""),
        customer_name: booking.customer_name,
        customer_phone: booking.customer_phone,
        recipient_name: isReturn ? booking.customer_name : (booking.recipient_name || booking.customer_name),
        recipient_phone: isReturn ? booking.customer_phone : (booking.recipient_phone || booking.customer_phone),
        pickup_code: isReturn ? null : booking.pickup_code,
        shop_name: shop?.shop_name,
        qr_data: `EMA:${booking.booking_number}|${booking.qr_token || booking.id}`,
        service_type: booking.delivery_type,
        is_return: isReturn,
      });
      toast.success(`Label printet på ${printer.name}`);
    } catch (e) {
      toast.error(e?.message || "Print-fejl");
    }
  };

  const submitPromptCode = async () => {
    if (!promptCode || promptCode.length !== 6) {
      toast.error("Pickup-koden er 6 tegn");
      return;
    }
    try {
      await axios.post(`${API}/pakkeshop/scan`, { code: promptCode.toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`✅ Udleveret: #${codePromptBooking.booking_number}`);
      setCodePromptBooking(null);
      setPromptCode("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forkert kode");
    }
  };

  const tabs = [
    { id: "parcels", label: `Pakker (${parcels.length})`, icon: Package },
    { id: "returns", label: `Returkø${returnCount > 0 ? ` (${returnCount})` : ""}`, icon: RotateCcw },
    { id: "history", label: "Historik", icon: History },
    { id: "stats", label: "Statistik", icon: BarChart3 },
    { id: "statements", label: "Opgørelser", icon: FileText },
    { id: "equipment", label: "Mit udstyr", icon: Cpu },
    { id: "flyer", label: "Flyer", icon: ClipboardList },
  ];

  return (
    <div className="min-h-screen bg-[#F1F5F9]" data-testid="shop-dashboard">
      <RealtimeBanner
        event={liveEvent}
        onDismiss={() => setLiveEvent(null)}
        onJumpToTab={(t) => { setTab(t); setLiveEvent(null); }}
      />
      <div className="bg-[#0A1128] text-white py-5 px-5">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">EMA Pickup Point Portal</div>
            <div className="font-display font-bold text-lg" data-testid="shop-name">{shop?.shop_name}</div>
            <div className="text-xs text-white/60 mt-0.5">{shop?.shop_address} · {shop?.shop_city}</div>
          </div>
          <button
            onClick={onLogout}
            className="text-white/80 hover:text-white text-sm flex items-center gap-1"
            data-testid="shop-logout"
          >
            <LogOut size={14} /> Log ud
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 pt-6">
        <div className="flex gap-1 border-b border-[#E2E8F0] overflow-x-auto" data-testid="shop-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
                  active
                    ? "border-brand-red text-[#0A1128] font-bold"
                    : "border-transparent text-[#475569] hover:text-[#0A1128]"
                }`}
                data-testid={`tab-${t.id}`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-6">
        <ScannerPrinterBar
          token={token}
          printer={printer}
          connectingPrinter={connectingPrinter}
          onConnectPrinter={handleConnectPrinter}
          onDisconnectPrinter={handleDisconnectPrinter}
          onCameraOpen={() => setCameraOpen(true)}
          onScan={handleScan}
        />
        {tab === "parcels" && (
          <ParcelsTab token={token} parcels={parcels} loading={loading} onReload={load} printer={printer} onBluetoothPrint={handleBluetoothPrint} />
        )}
        {tab === "returns" && <ReturnsTab token={token} onReload={load} printer={printer} onBluetoothPrint={handleBluetoothPrint} />}
        {tab === "history" && <HistoryTab token={token} />}
        {tab === "stats" && <StatsTab token={token} />}
        {tab === "statements" && <StatementsTab token={token} shop={shop} />}
        {tab === "equipment" && <EquipmentTab token={token} />}
        {tab === "flyer" && <FlyerTab token={token} shop={shop} />}

        <p className="text-xs text-[#475569] mt-8 text-center leading-relaxed bg-white p-4 border border-[#E2E8F0] rounded-xl">
          Husk: pickup-koden er kundens kvittering — uden den må pakken IKKE udleveres. Ved tvivl, ring til EMA Kurer på +45 70 60 50 40.
        </p>
      </div>

      <CameraQRScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onScan={(code) => { setCameraOpen(false); handleScan(code); }}
      />

      {codePromptBooking && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5" onClick={() => setCodePromptBooking(null)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-xl p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="code-prompt-modal">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Pakke scannet</div>
            <h3 className="font-display font-bold text-xl text-[#0A1128] mt-2">#{codePromptBooking.booking_number}</h3>
            <p className="text-sm text-[#475569] mt-1">{codePromptBooking.customer_name}</p>
            <p className="text-sm text-[#0A1128] mt-4">Indtast eller scan kundens 6-tegns pickup-kode:</p>
            <input
              autoFocus
              value={promptCode}
              onChange={(e) => setPromptCode(e.target.value.toUpperCase().slice(0, 6))}
              onKeyDown={(e) => e.key === "Enter" && submitPromptCode()}
              maxLength={6}
              className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 mt-3 font-mono text-2xl uppercase tracking-[8px] text-center focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red"
              data-testid="code-prompt-input"
              data-scanner-input="true"
            />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setCodePromptBooking(null)} className="flex-1 py-3 border border-[#E2E8F0] rounded-lg text-sm font-semibold text-[#475569]">Annullér</button>
              <button onClick={submitPromptCode} className="btn-primary flex-1 justify-center" data-testid="code-prompt-submit">
                <CheckCircle2 size={14} /> Udlever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PakkeshopPortalPage() {
  const [token, setToken] = React.useState(() => localStorage.getItem(TOKEN_KEY));
  const [shop, setShop] = React.useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SHOP_KEY) || "null");
    } catch {
      return null;
    }
  });

  React.useEffect(() => {
    document.title = "EMA Pickup Point Portal · EMA Kurer ApS";
  }, []);

  // When wrapped as native shop app, register push + flush pending token after login.
  React.useEffect(() => {
    if (!token || !shop) return;
    if (!isNative()) return;
    (async () => {
      await flushPendingShopToken();
      await initShopNativePush();
    })();
  }, [token, shop]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(SHOP_KEY);
    setToken(null);
    setShop(null);
  };

  if (!token || !shop) {
    return <LoginCard onLogin={(t, s) => { setToken(t); setShop(s); }} />;
  }
  return <ShopDashboard token={token} shop={shop} onLogout={logout} />;
}
